import { useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

interface ScopeResponse {
  valid: boolean;
  scope: string;
}

export interface AdminTokenScope {
  scope: string;
  isFullAdmin: boolean;
  isLoading: boolean;
}

export function useAdminTokenScope(token: string | null | undefined): AdminTokenScope {
  // Fall back to localStorage when the URL token has been stripped by replaceState.
  // useAdminPageToken() already resolves localStorage before calling this hook, so
  // this guard is a secondary safety net for callers that pass useParams() directly.
  const storedToken =
    typeof window !== "undefined" ? (localStorage.getItem("ngu_admin_token") ?? "") : "";
  const effectiveToken = token || storedToken;

  const { data, isLoading } = useQuery<ScopeResponse>({
    queryKey: ["admin-token-scope", effectiveToken],
    queryFn: async () => {
      const r = await fetch("/api/admin/validate-token", {
        headers: { "x-admin-key": effectiveToken || "" },
      });
      if (r.status === 503) {
        // DB temporarily unavailable — throw so React Query retries with backoff
        // rather than returning {valid:false} which shows "Access Denied" on every card.
        throw new Error("service-unavailable");
      }
      if (!r.ok) return { valid: false, scope: "" };
      const result = await r.json();
      if (result.valid && effectiveToken) {
        try {
          localStorage.setItem("ngu_admin_token", effectiveToken);
        } catch {
          // intentionally silent: localStorage unavailable in private browsing — token is still in URL
        }
      }
      return result;
    },
    enabled: !!effectiveToken,
    staleTime: 300000,
    retry: (failureCount, error) => {
      // Retry on service-unavailable (DB pool exhaustion) up to 5 times with backoff.
      // Never retry genuine auth failures (401/403) — those are not recoverable by retrying.
      if (error instanceof Error && error.message === "service-unavailable") {
        return failureCount < 5;
      }
      return false;
    },
    retryDelay: (attempt) => Math.min(1000 * Math.pow(2, attempt), 10000),
  });

  const scope = data?.scope ?? "";
  const isFullAdmin = isLoading ? true : scope === "full";

  // Prefetch the admin-pages chunk whenever a valid full-admin token is
  // confirmed. On pages that are not in the admin-pages chunk (e.g. NGU hub
  // pages) this kicks off the download in the background so the first admin
  // navigation is instant. When already inside an admin page the dynamic
  // import resolves from the browser module cache — effectively a no-op.
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (!isLoading && isFullAdmin && !prefetchedRef.current) {
      prefetchedRef.current = true;
      import("@/pages/AdminToday").catch(() => {});
    }
  }, [isLoading, isFullAdmin]);

  return { scope, isFullAdmin, isLoading };
}
